import { Navigate } from 'react-router';
import { useIsReadOnly, useWorkspaceRole } from '../../hooks/user/useWorkspaceRole';
import type { ReactNode } from 'react';

/**
 * WriterRoute — redirects read-only workspace roles (GUEST/VIEWER) away
 * from pages that have no meaningful read-only mode (Trash, Audit logs,
 * version history, etc.).
 *
 * Pair with `WorkspaceRoute` so the workspace is loaded before the
 * read-only check fires; the role only resolves after `useWorkspace`
 * returns the workspace shape.
 *
 * If the workspace hasn't yet resolved the role (`useWorkspaceRole`
 * returns `null` on first paint), we render the children optimistically
 * — the page itself won't render write controls before the role
 * resolves and the redirect kicks in on the next render. This avoids
 * flashing a redirect for legitimate writers on slow networks.
 */
interface WriterRouteProps {
  children: ReactNode;
  /** Where to send blocked viewers. Defaults to the dashboard. */
  redirectTo?: string;
}

export function WriterRoute({ children, redirectTo = '/dashboard' }: WriterRouteProps) {
  const role = useWorkspaceRole();
  const isReadOnlyRole = useIsReadOnly();

  // Role hasn't resolved yet — render children. They won't expose
  // write controls (those use `useIsReadOnly()` defensively). The redirect
  // fires on the next render once the read-only role resolves.
  if (role === null) {
    return <>{children}</>;
  }

  if (isReadOnlyRole) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
